// @ts-nocheck
// ============================================
// FILE: mp-media-gallery-autoplay.js
// PURPOSE: Auto-plays videos in the product media gallery when their
//          carousel slide becomes visible. Activates only when the
//          'video_autoplay' block setting is enabled.
// ADDED: 2026-04-07 by MeshPlanet
// UPDATED: 2026-04-13 — IO-based approach with guards to never interfere
//          with user-initiated playback via the manual play button.
// ============================================

/**
 * Fully unloads a deferred-media element back to its initial poster state.
 * @param {Element} deferredMedia
 */
function unloadDeferredMedia(deferredMedia) {
  if (!deferredMedia) return;
  if (!deferredMedia.getAttribute('data-media-loaded')) return;

  if (typeof deferredMedia.pauseMedia === 'function') {
    deferredMedia.pauseMedia();
  } else {
    deferredMedia.querySelector('video')?.pause();
  }

  deferredMedia.querySelector('video')?.remove();
  deferredMedia.querySelector('iframe')?.remove();

  deferredMedia.removeAttribute('data-media-loaded');
  deferredMedia.isPlaying = false;

  deferredMedia
    .querySelector('[ref="deferredMediaPlayButton"]')
    ?.classList.remove('deferred-media__playing');

  deferredMedia
    .querySelector('[ref="toggleMediaButton"]')
    ?.classList.add('hidden');
}

/**
 * Initialises autoplay behaviour on a single <media-gallery> element.
 *
 * Uses IntersectionObserver to detect when a video slide scrolls into view.
 * Key rules:
 *  - NEVER touch a deferred-media that is already loaded (data-media-loaded).
 *    That means the user tapped the play button manually — leave it alone.
 *  - If programmatic autoplay fails (promise rejected), fully reset via
 *    unloadDeferredMedia so the manual play button remains functional, and
 *    set a flag to avoid retrying in a loop.
 *  - When the slide scrolls out of view, unload the video regardless of how
 *    it was started, so it restarts fresh on the next visit.
 *
 * @param {Element} gallery
 */
function initGalleryAutoplay(gallery) {
  if (gallery.dataset.presentation !== 'carousel') return;
  if (gallery.dataset.mpAutoplayInit === 'true') return;
  gallery.dataset.mpAutoplayInit = 'true';

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const slide = entry.target;
        const deferredMedia = slide.querySelector('deferred-media');
        if (!deferredMedia) continue;

        if (entry.isIntersecting) {
          // Already loaded (user tapped play) — don't interfere
          if (deferredMedia.getAttribute('data-media-loaded')) continue;
          // Already tried and failed on this visit — don't retry
          if (slide.dataset.autoplayFailed) continue;

          // --- Attempt programmatic autoplay ---
          deferredMedia.loadContent?.(false);

          const video = deferredMedia.querySelector('video');
          if (video) {
            // Force attributes required for mobile autoplay
            video.muted = true;
            video.setAttribute('muted', '');
            video.playsInline = true;
            video.setAttribute('playsinline', '');

            const playPromise = video.play();
            if (playPromise) {
              playPromise
                .then(() => {
                  deferredMedia.isPlaying = true;
                  deferredMedia.updatePlayPauseHint?.(true);
                })
                .catch(() => {
                  // Autoplay blocked — fully reset so the manual play button
                  // still works (loadContent can run fresh on user tap).
                  slide.dataset.autoplayFailed = 'true';
                  unloadDeferredMedia(deferredMedia);
                });
            }
          } else {
            // iframe (YouTube/Vimeo) — autoplay is in the src URL
            deferredMedia.isPlaying = true;
            deferredMedia.updatePlayPauseHint?.(true);
          }
        } else {
          // Slide left the viewport — unload and reset flags
          unloadDeferredMedia(deferredMedia);
          delete slide.dataset.autoplayFailed;
        }
      }
    },
    {
      // Use the viewport (root: null) instead of the scroll container.
      // Some mobile browsers (iOS Safari) have issues with custom-element
      // scroll containers as IO root.
      threshold: 0.5,
    }
  );

  // Observe every slide that contains a video
  gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
    if (slide.querySelector('deferred-media')) {
      observer.observe(slide);
    }
  });
}

/**
 * Finds all galleries with video autoplay and sets up observers.
 */
function initAllGalleries() {
  document
    .querySelectorAll('media-gallery[data-video-autoplay="true"]')
    .forEach(initGalleryAutoplay);
}

// Initial page load
initAllGalleries();

// Shopify editor live preview
document.addEventListener('shopify:section:load', initAllGalleries);
document.addEventListener('shopify:block:select', initAllGalleries);

// Variant changes replace the entire <media-gallery> element
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node.nodeType !== Node.ELEMENT_NODE) continue;
      if (node.matches?.('media-gallery[data-video-autoplay="true"]')) {
        initGalleryAutoplay(node);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
